import React, { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { CerfaState, DossierDetail, DossierDocument, DossierSummary, GarageProfile } from "../types";
import { API_URL } from "../services/api";
import {
  archiveDossier,
  archiveDossiers,
  createDossier,
  deleteDossier,
  deleteDossierDocument,
  getDossier,
  getStorageUsage,
  listDossiers,
  uploadDossierDocument,
  computeDossierValuation,
  uploadControleTechnique,
  deleteControleTechnique,
} from "../services/dossierService";
import { startCheckoutAddon } from "../services/billingService";
import { fetchGarageProfile } from "../services/profileService";

const statusColors: Record<string, string> = {
  draft: "bg-slate-200 text-slate-800",
  in_progress: "bg-amber-100 text-amber-800",
  ready: "bg-emerald-100 text-emerald-800",
  archived: "bg-slate-300 text-slate-700",
};

const DossiersPage: React.FC = () => {
  const [items, setItems] = useState<DossierSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statut, setStatut] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<DossierDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [ctUploading, setCtUploading] = useState(false);
  const [ctError, setCtError] = useState<string | null>(null);
  const [ctDeleting, setCtDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState<{ used_bytes: number; quota_bytes: number; percent: number } | null>(null);
  const [archiveUrl, setArchiveUrl] = useState<string | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split("T")[0]);
  const [invoiceHT, setInvoiceHT] = useState("");
  const [invoiceTva, setInvoiceTva] = useState("20");
  const invoiceRef = useRef<HTMLDivElement | null>(null);
  const [garageProfile, setGarageProfile] = useState<GarageProfile | null>(null);
  const [valuationLoading, setValuationLoading] = useState(false);

  const loadUsage = async () => {
    try {
      const data = await getStorageUsage();
      setUsage(data);
    } catch (e) {
      console.error(e);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listDossiers({ statut, search: search || undefined });
      setItems(data);
      if (selected) {
        const detail = await getDossier(selected.id);
        setSelected(detail);
      }
      loadUsage();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    loadUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statut]);

  useEffect(() => {
    void (async () => {
      const gp = await fetchGarageProfile();
      if (gp) setGarageProfile(gp);
    })();
  }, []);

  const triggerValuation = async () => {
    if (!selected) return;
    setValuationLoading(true);
    try {
      await computeDossierValuation(selected.id);
      const detail = await getDossier(selected.id);
      setSelected(detail);
    } catch (e) {
      console.error("Valuation error", e);
    } finally {
      setValuationLoading(false);
    }
  };

  const onCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const { id } = await createDossier({ titre: newTitle.trim(), statut: "draft" });
      setNewTitle("");
      const detail = await getDossier(id);
      setSelected(detail);
      refresh();
      loadUsage();
    } catch (e) {
      console.error(e);
    } finally {
      setCreating(false);
    }
  };

  const onSelect = async (id: number) => {
    try {
      const detail = await getDossier(id);
      setSelected(detail);
      const today = new Date().toISOString().split("T")[0];
      setInvoiceDate(today);
      setInvoiceNumber(`INV-${id}-${today.replace(/-/g, "")}`);
    } catch (e) {
      console.error(e);
    }
  };

  const onUpload = async (file: File, type: string) => {
    if (!selected) return;
    setUploading(true);
    setUploadError(null);
    try {
      await uploadDossierDocument(selected.id, file, type);
      const detail = await getDossier(selected.id);
      setSelected(detail);
      loadUsage();
    } catch (e: any) {
      console.error(e);
      const msg = e?.message || "Upload échoué (fichier non autorisé ?).";
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  };

  const onUploadCt = async (file: File) => {
    if (!selected) return;
    setCtUploading(true);
    setCtError(null);
    try {
      await uploadControleTechnique(selected.id, file);
      const detail = await getDossier(selected.id);
      setSelected(detail);
      loadUsage();
    } catch (e: any) {
      console.error(e);
      setCtError(e?.message || "Erreur OCR contrôle technique");
    } finally {
      setCtUploading(false);
    }
  };

  const onDeleteCt = async () => {
    if (!selected) return;
    setCtDeleting(true);
    setCtError(null);
    try {
      await deleteControleTechnique(selected.id);
      const detail = await getDossier(selected.id);
      setSelected(detail);
    } catch (e: any) {
      console.error(e);
      setCtError(e?.message || "Erreur suppression CT");
    } finally {
      setCtDeleting(false);
    }
  };

  const onDeleteDoc = async (doc: DossierDocument) => {
    if (!selected) return;
    setBusy(true);
    try {
      await deleteDossierDocument(selected.id, doc.id);
      const detail = await getDossier(selected.id);
      setSelected(detail);
      loadUsage();
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const onDeleteDossier = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await deleteDossier(selected.id);
      setSelected(null);
      refresh();
      loadUsage();
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const onArchiveSelected = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      // On n'efface pas les documents (cleanup=false) pour conserver CG/CI/Cerfa/facture dans le dossier
      const { url } = await archiveDossier(selected.id, false);
      setArchiveUrl(url.startsWith("http") ? url : `${API_URL}${url}`);
      const detail = await getDossier(selected.id);
      setSelected(detail);
      loadUsage();
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const onArchiveAll = async () => {
    if (!items.length) return;
    setBusy(true);
    try {
      const ids = items.map((d) => d.id);
      const { url } = await archiveDossiers(ids, false);
      setArchiveUrl(url.startsWith("http") ? url : `${API_URL}${url}`);
      loadUsage();
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const getParty = (cerfa?: CerfaState | null, role: "seller" | "buyer" = "buyer") => {
    if (!cerfa) return null;
    const p = cerfa[role];
    return p || null;
  };

  const formattedAmount = (value: string) => {
    const num = parseFloat(value.replace(",", "."));
    if (isNaN(num)) return 0;
    return num;
  };

  const generateInvoice = async () => {
    if (!selected) return;
    const element = invoiceRef.current;
    if (!element) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = 210;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      const blob = pdf.output("blob");
      const rawName = invoiceNumber || `INV-${selected.id}`;
      const rawDate = invoiceDate || new Date().toISOString().split("T")[0];
      const ref = selected.immatriculation || selected.vin || `dossier-${selected.id}`;
      const slug = (str: string) => str.replace(/[^a-zA-Z0-9-_]/g, "-");
      const fileName = `facture_${slug(rawName)}_${slug(ref)}_${rawDate}.pdf`;
      const file = new File([blob], fileName, { type: "application/pdf" });
      await uploadDossierDocument(selected.id, file, "facture");
      const detail = await getDossier(selected.id);
      setSelected(detail);
    } catch (e) {
      console.error("Erreur génération facture", e);
    } finally {
      setBusy(false);
    }
  };

  const formatBytes = (val?: number) => {
    if (!val) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let idx = 0;
    let num = val;
    while (num >= 1024 && idx < units.length - 1) {
      num /= 1024;
      idx++;
    }
    return `${num.toFixed(1)} ${units[idx]}`;
  };

  return (
    <React.Fragment>
      <div className="min-h-screen bg-slate-950 text-white p-4 md:p-8">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1 bg-slate-900/70 border border-slate-800 rounded-xl p-4 space-y-3">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-xs text-slate-200">
            <div className="flex justify-between items-center mb-1">
              <span>Espace utilisé</span>
              {usage && <span className="font-semibold">{usage.percent}%</span>}
            </div>
            <div className="w-full h-2 rounded bg-slate-700 overflow-hidden">
              <div
                className="h-2 bg-sky-500"
                style={{ width: `${Math.min(usage?.percent || 0, 100)}%` }}
              ></div>
            </div>
            <div className="mt-1 text-[11px] text-slate-400">
              {usage ? `${formatBytes(usage.used_bytes)} / ${formatBytes(usage.quota_bytes)}` : ""}
            </div>
            <button
              onClick={async () => {
                try {
                  await startCheckoutAddon();
                } catch (e) {
                  console.error(e);
                }
              }}
              className="mt-2 w-full text-center bg-emerald-600 hover:bg-emerald-500 rounded-md py-2 text-[11px] font-semibold"
            >
              Ajouter +10 Go (2,99€/mois)
            </button>
            <button
              onClick={onArchiveAll}
              disabled={busy}
              className="mt-2 w-full text-center bg-slate-700 hover:bg-slate-600 rounded-md py-2 text-[11px] border border-slate-600"
            >
              Exporter les dossiers en ZIP
            </button>
            {archiveUrl && (
              <a href={archiveUrl} target="_blank" rel="noreferrer" className="text-sky-400 underline text-[11px]">
                Télécharger le dernier ZIP
              </a>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && refresh()}
              placeholder="Recherche immat, VIN, client…"
              className="w-full rounded-md bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white focus:outline-none"
            />
            <button
              onClick={refresh}
              className="px-3 py-2 bg-slate-800 rounded-md text-sm border border-slate-700 hover:bg-slate-700"
            >
              Go
            </button>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {["draft", "in_progress", "ready", "archived"].map((s) => (
              <button
                key={s}
                onClick={() => setStatut(statut === s ? undefined : s)}
                className={`px-3 py-1 rounded-full border ${
                  statut === s ? "bg-sky-600 border-sky-500" : "border-slate-700 bg-slate-800"
                }`}
              >
                {s}
              </button>
            ))}
            <button
              onClick={() => {
                setStatut(undefined);
                setSearch("");
                refresh();
              }}
              className="px-3 py-1 rounded-full border border-slate-700 bg-slate-800"
            >
              Réinitialiser
            </button>
          </div>

          <div className="border-t border-slate-800 pt-3">
            <p className="text-xs text-slate-300 mb-2">Nouveau dossier</p>
            <div className="flex gap-2">
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Ex : Vente Clio Blanche"
                className="w-full rounded-md bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white focus:outline-none"
              />
              <button
                onClick={onCreate}
                disabled={creating}
                className="px-3 py-2 bg-sky-600 rounded-md text-sm font-semibold hover:bg-sky-500 disabled:opacity-60"
              >
                +
              </button>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-3 space-y-2 max-h-[60vh] overflow-auto">
            {loading && <div className="text-sm text-slate-400">Chargement…</div>}
            {!loading &&
              items.map((d) => (
                <div
                  key={d.id}
                  onClick={() => onSelect(d.id)}
                  className={`p-3 rounded-lg border ${
                    selected?.id === d.id ? "border-sky-500 bg-slate-800" : "border-slate-800 bg-slate-900/80"
                  } cursor-pointer`}
                >
                  <div className="flex justify-between items-center">
                    <div className="font-semibold text-sm">{d.titre || d.immatriculation || `Dossier #${d.id}`}</div>
                    <span className={`text-xs px-2 py-1 rounded-full ${statusColors[d.statut || "draft"] || ""}`}>
                      {d.statut || "draft"}
                    </span>
                  </div>
                  <div className="text-xs text-slate-400">
                    {d.immatriculation} • {d.vin}
                  </div>
                </div>
              ))}
            {!loading && items.length === 0 && <div className="text-sm text-slate-500">Aucun dossier.</div>}
          </div>
        </div>

        <div className="md:col-span-2 bg-slate-900/70 border border-slate-800 rounded-xl p-4 min-h-[70vh]">
          {!selected && <div className="text-slate-400 text-sm">Sélectionnez ou créez un dossier.</div>}
          {selected && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3 items-center">
                <div>
                  <div className="text-lg font-semibold">{selected.titre || selected.immatriculation || `Dossier #${selected.id}`}</div>
                  <div className="text-xs text-slate-400">
                    {selected.immatriculation} • {selected.vin}
                  </div>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full ${statusColors[selected.statut || "draft"] || ""}`}>
                  {selected.statut || "draft"}
                </span>
                <a
                  href={`/?dossierId=${selected.id}`}
                  className="px-3 py-1 rounded bg-slate-200 text-slate-900 text-xs font-semibold hover:bg-slate-100 transition"
                >
                  Ouvrir le Cerfa
                </a>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-slate-300 font-semibold">Documents</p>
                <div className="flex gap-2 items-center text-xs text-slate-300">
                  <label className="cursor-pointer bg-slate-800 border border-slate-700 px-3 py-2 rounded-md hover:bg-slate-700">
                    Ajouter un document
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onUpload(file, "justificatif");
                        e.target.value = "";
                      }}
                    />
                  </label>
                  <label className="cursor-pointer bg-indigo-700 border border-indigo-500 px-3 py-2 rounded-md hover:bg-indigo-600">
                    Scanner contrôle technique (OCR)
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onUploadCt(file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {uploading && <span>Envoi...</span>}
                  {uploadError && <span className="text-red-300 text-xs">{uploadError}</span>}
                  {ctUploading && <span>Analyse CT...</span>}
                  {ctError && <span className="text-red-300 text-xs">{ctError}</span>}
                </div>
                <div className="space-y-2">
                  {selected.documents?.map((doc: DossierDocument) => {
                    const url = doc.url.startsWith("http") ? doc.url : `${API_URL}${doc.url}`;
                    return (
                      <div key={doc.id} className="flex justify-between items-center text-sm bg-slate-800/70 px-3 py-2 rounded-md">
                        <div>
                          <div className="font-semibold">{doc.filename}</div>
                          <div className="text-xs text-slate-400">{doc.type}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a href={url} target="_blank" rel="noreferrer" className="text-sky-400 text-xs underline">
                            Ouvrir
                          </a>
                          <button
                            onClick={() => onDeleteDoc(doc)}
                            className="text-xs text-red-300 hover:text-red-200"
                            disabled={busy}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {(!selected.documents || selected.documents.length === 0) && (
                  <div className="text-xs text-slate-500">Aucun document pour le moment.</div>
                )}
              </div>
              {selected.ct_data && (
                <div className="mt-4 p-3 bg-slate-800/60 border border-slate-700 rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-sm font-semibold">Contrôle technique</div>
                      <div className="text-[11px] text-slate-400">
                        PV {selected.ct_data.pv_number || "-"} • Centre {selected.ct_data.center_name || "?"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[11px] px-2 py-1 rounded ${
                          selected.ct_data.result === "favorable"
                            ? "bg-emerald-600/30 text-emerald-200"
                            : selected.ct_data.result === "contre_visite"
                            ? "bg-amber-600/30 text-amber-200"
                            : "bg-red-600/30 text-red-200"
                        }`}
                      >
                        {selected.ct_data.result || "?"}
                      </span>
                      <button
                        onClick={onDeleteCt}
                        disabled={ctDeleting}
                        className="text-[11px] px-2 py-1 rounded bg-red-700 hover:bg-red-600 disabled:opacity-60"
                      >
                        {ctDeleting ? "Suppression..." : "Supprimer"}
                      </button>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-2 text-xs text-slate-200">
                    <div className="p-2 bg-slate-900/60 rounded border border-slate-700">
                      <div className="text-[10px] text-slate-400">Immatriculation</div>
                      <div className="font-semibold">{selected.ct_data.registration_number || "-"}</div>
                    </div>
                    <div className="p-2 bg-slate-900/60 rounded border border-slate-700">
                      <div className="text-[10px] text-slate-400">VIN</div>
                      <div className="font-semibold">{selected.ct_data.vin || "-"}</div>
                    </div>
                    <div className="p-2 bg-slate-900/60 rounded border border-slate-700">
                      <div className="text-[10px] text-slate-400">Km relevé</div>
                      <div className="font-semibold">
                        {selected.ct_data.mileage_km ? `${selected.ct_data.mileage_km} km` : "-"}
                      </div>
                    </div>
                    <div className="p-2 bg-slate-900/60 rounded border border-slate-700">
                      <div className="text-[10px] text-slate-400">Date visite</div>
                      <div className="font-semibold">{selected.ct_data.ct_date || "-"}</div>
                    </div>
                    <div className="p-2 bg-slate-900/60 rounded border border-slate-700">
                      <div className="text-[10px] text-slate-400">Validité</div>
                      <div className="font-semibold">{selected.ct_data.valid_until || "-"}</div>
                    </div>
                    <div className="p-2 bg-slate-900/60 rounded border border-slate-700">
                      <div className="text-[10px] text-slate-400">Centre</div>
                      <div className="font-semibold">
                        {selected.ct_data.center_name || "-"} {selected.ct_data.center_siret ? `(${selected.ct_data.center_siret})` : ""}
                      </div>
                    </div>
                  </div>
                  {(() => {
                    const ct: any = selected.ct_data || {};
                    const critical = ct.defects_critical || [];
                    const major = ct.defects_major || [];
                    const minor = ct.defects_minor || [];
                    const generic = ct.defects || [];
                    const renderList = (title: string, items: any[], badge: string) => {
                      if (!items || !items.length) return null;
                      return (
                        <div className="mt-3 text-xs text-slate-200">
                          <div className="font-semibold mb-1 flex items-center gap-2">
                            {title}
                            <span className="px-2 py-0.5 rounded bg-slate-700 text-[10px]">{badge}</span>
                          </div>
                          <ul className="space-y-1">
                            {items.slice(0, 5).map((d: any, idx: number) => (
                              <li key={idx} className="flex gap-2 items-start">
                                <span className="px-2 py-0.5 rounded bg-slate-800 text-[10px]">
                                  {d.severity || d.code || badge}
                                </span>
                                <span className="text-[11px] text-slate-300 truncate">{d.label || d.text || "-"}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    };
                    return (
                      <>
                        {renderList("Défaillances critiques", critical, "C")}
                        {renderList("Défaillances majeures", major, "S")}
                        {renderList("Défaillances mineures", minor, "A")}
                        {!critical.length && !major.length && !minor.length && generic.length > 0 && renderList("Défauts relevés", generic, "?")}
                      </>
                    );
                  })()}
                  {selected.ct_data.observations && (
                    <div className="mt-2 text-[11px] text-slate-300">
                      <span className="font-semibold">Observations :</span> {selected.ct_data.observations}
                    </div>
                  )}
                </div>
              )}
              <div className="mt-4 p-3 bg-slate-800/60 border border-slate-700 rounded-md">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold">Estimation valeur</div>
                    <div className="text-[11px] text-slate-400">Fourchette marché + reprise</div>
                  </div>
                  <button
                    onClick={triggerValuation}
                    disabled={valuationLoading || busy}
                    className="bg-indigo-600 hover:bg-indigo-500 text-xs px-3 py-2 rounded-md font-semibold disabled:opacity-60"
                  >
                    {valuationLoading ? "Calcul..." : "Estimer"}
                  </button>
                </div>
                {selected.valuation ? (
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-200">
                    <div className="p-2 bg-slate-900/70 rounded border border-slate-700">
                      <div className="text-[10px] text-slate-400">Marché (basse)</div>
                      <div className="font-semibold">{selected.valuation.market_low} €</div>
                    </div>
                    <div className="p-2 bg-slate-900/70 rounded border border-slate-700">
                      <div className="text-[10px] text-slate-400">Marché (haute)</div>
                      <div className="font-semibold">{selected.valuation.market_high} €</div>
                    </div>
                    <div className="p-2 bg-slate-900/70 rounded border border-slate-700">
                      <div className="text-[10px] text-slate-400">Médiane</div>
                      <div className="font-semibold">{selected.valuation.market_median} €</div>
                    </div>
                    <div className="p-2 bg-slate-900/70 rounded border border-slate-700">
                      <div className="text-[10px] text-slate-400">Reprise</div>
                      <div className="font-semibold">{selected.valuation.tradein} €</div>
                    </div>
                    <div className="col-span-2 text-[10px] text-slate-500">
                      Calculé le {selected.valuation.computed_at?.slice(0, 10)} • Source interne
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">Pas encore d'estimation.</div>
                )}
              </div>
                {/* Facture */}
                <div className="mt-4 p-3 bg-slate-800/60 border border-slate-700 rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-sm font-semibold">Générer la facture de vente</div>
                      <div className="text-[11px] text-slate-400">Pré-remplie avec les infos CG/ID du Cerfa</div>
                    </div>
                    <button
                      onClick={generateInvoice}
                      disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-500 text-xs px-3 py-2 rounded-md font-semibold disabled:opacity-60"
                    >
                      Générer & ajouter au dossier
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <label className="text-[11px] text-slate-400">Numéro de facture</label>
                      <input
                        className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400">Date</label>
                      <input
                        type="date"
                        className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
                        value={invoiceDate}
                        onChange={(e) => setInvoiceDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400">Montant HT (€)</label>
                      <input
                        className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
                        value={invoiceHT}
                        onChange={(e) => setInvoiceHT(e.target.value)}
                        placeholder="Ex: 12500"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400">TVA (%)</label>
                      <input
                        className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
                        value={invoiceTva}
                        onChange={(e) => setInvoiceTva(e.target.value)}
                        placeholder="20"
                      />
                    </div>
                  </div>
                </div>
                <div className="pt-2">
                  <button
                    onClick={onArchiveSelected}
                    disabled={busy}
                    className="text-xs text-sky-300 hover:text-sky-200 underline mr-4"
                  >
                    Archiver ce dossier (ZIP)
                  </button>
                  <button
                    onClick={onDeleteDossier}
                    disabled={busy}
                    className="text-xs text-red-300 hover:text-red-200 underline"
                  >
                    Supprimer le dossier
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
      {/* Template facture (capturé via html2canvas) */}
      {selected && (
        <div className="fixed -left-[9999px] top-0 w-[210mm] bg-white text-black p-8" ref={invoiceRef}>
          {(() => {
            const cerfa = (selected.cerfa_state as CerfaState) || null;
            const seller = getParty(cerfa, "seller");
            const buyer = getParty(cerfa, "buyer");
            const garage = garageProfile;
            const veh = cerfa?.vehicle;
            const ht = formattedAmount(invoiceHT);
            const tva = formattedAmount(invoiceTva);
            const ttc = ht + (ht * tva) / 100;
            const fmtParty = (p: any, fallbackName?: string, fallbackAddr?: string) => {
              const name = p?.isCompany
                ? p.companyName
                : [p?.firstName, p?.lastName].filter(Boolean).join(" ");
              const addr =
                p?.address ||
                [p?.addressLine1, p?.addressLine2].filter(Boolean).join(" ") ||
                fallbackAddr ||
                "";
              const city = [p?.postalCode, p?.city].filter(Boolean).join(" ");
              return { name: name || fallbackName || "", addr, city, siret: p?.siret };
            };
            const sellerFmt = fmtParty(
              seller,
              garage?.name,
              garage?.address
            );
            const sellerName = garage?.name || sellerFmt.name || seller?.lastName;
            const sellerAddr = garage?.address || sellerFmt.addr;
            const sellerCity = [garage?.postalCode || seller?.postalCode || sellerFmt.city].filter(Boolean).join(" ");
            const sellerSiret = garage?.siret || sellerFmt.siret || seller?.siret;
            const buyerFmt = fmtParty(
              buyer,
              [selected.client_prenom, selected.client_nom].filter(Boolean).join(" "),
              ""
            );
            return (
              <div style={{ fontFamily: "Arial, sans-serif", fontSize: "11px", color: "#111" }}>
                <div className="flex justify-between mb-4">
                  <div>
                    <div className="text-lg font-bold">FACTURE</div>
                    <div className="text-sm text-gray-600">N° {invoiceNumber}</div>
                    <div className="text-sm text-gray-600">Date : {invoiceDate}</div>
                  </div>
                  <div className="text-right">
                    {garage?.logoData && (
                      <img src={garage.logoData} alt="Logo garage" style={{ maxHeight: "48px", maxWidth: "140px", objectFit: "contain", marginLeft: "auto" }} />
                    )}
                    <div className="font-semibold">
                      {sellerName}
                    </div>
                    <div>{sellerAddr}</div>
                    <div>{sellerCity}</div>
                    {sellerSiret && <div>SIRET : {sellerSiret}</div>}
                  </div>
                </div>

                <div className="mb-3">
                  <div className="font-semibold text-sm">Facturé à</div>
                  <div>{buyerFmt.name || "Acheteur"}</div>
                  <div>{buyerFmt.addr || "Adresse non renseignée"}</div>
                  <div>{buyerFmt.city}</div>
                </div>

                <div className="mb-3">
                  <div className="font-semibold text-sm">Véhicule</div>
                  <div>{[veh?.brand, veh?.model].filter(Boolean).join(" ")}</div>
                  <div>Immatriculation : {veh?.registrationNumber || selected.immatriculation}</div>
                  <div>VIN : {veh?.vin || selected.vin}</div>
                  <div>Date 1ère immat. : {veh?.firstRegistrationDate}</div>
                  <div>Genre : {veh?.genre}</div>
                </div>

                <table className="w-full mb-4" style={{ borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid #ddd", padding: "6px", textAlign: "left" }}>Description</th>
                      <th style={{ border: "1px solid #ddd", padding: "6px", textAlign: "right" }}>Montant HT (€)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ border: "1px solid #ddd", padding: "6px" }}>
                        Vente véhicule d'occasion {veh?.brand} {veh?.model} ({veh?.registrationNumber || selected.immatriculation})
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "6px", textAlign: "right" }}>{ht.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>

                <div className="flex justify-end">
                  <table style={{ borderCollapse: "collapse", minWidth: "220px" }}>
                    <tbody>
                      <tr>
                        <td style={{ border: "1px solid #ddd", padding: "6px" }}>Montant HT</td>
                        <td style={{ border: "1px solid #ddd", padding: "6px", textAlign: "right" }}>{ht.toFixed(2)} €</td>
                      </tr>
                      <tr>
                        <td style={{ border: "1px solid #ddd", padding: "6px" }}>TVA ({tva.toFixed(2)}%)</td>
                        <td style={{ border: "1px solid #ddd", padding: "6px", textAlign: "right" }}>{(ht * tva / 100).toFixed(2)} €</td>
                      </tr>
                      <tr>
                        <td style={{ border: "1px solid #ddd", padding: "6px", fontWeight: "bold" }}>Total TTC</td>
                        <td style={{ border: "1px solid #ddd", padding: "6px", textAlign: "right", fontWeight: "bold" }}>{ttc.toFixed(2)} €</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: "16px", fontSize: "10px", color: "#333", lineHeight: 1.4 }}>
                  <div style={{ fontWeight: "bold", marginBottom: "6px" }}>Conditions de vente véhicule d’occasion</div>
                  <ul style={{ paddingLeft: "16px", margin: 0 }}>
                    <li>Vente conclue le {cerfa?.saleDate || invoiceDate} à {cerfa?.saleTime || ""}.</li>
                    <li>Véhicule vendu en l’état, kilométrage indicatif, sans garantie autre que légale.</li>
                    <li>Déclaration sur l’honneur : véhicule non gagé et non déclaré volé à la date de vente.</li>
                    <li>Paiement reçu (ou à recevoir) selon conditions convenues entre vendeur et acheteur.</li>
                    <li>La cession ne sera effective qu’après encaissement intégral du prix.</li>
                  </ul>
                  <div style={{ marginTop: "10px", fontSize: "9px", color: "#666" }}>
                    Document préparé avec AutoCerfa pour faciliter la gestion des dossiers véhicule (www.autocerfa.eu).
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </React.Fragment>
  );
};

export default DossiersPage;
